import { Patch, produce } from "immer";
import { atom, useStore } from "jotai";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useCallback, useMemoOne } from "use-memo-one";

type UndoState = {
    undoStack: LayoutEditPatch[][];
    changesToCommit: LayoutEditPatch[];
};

export type LayoutEditPatch = {
    // null means the toplevel state
    block: string | null;
    inversePatches: Patch[];
};

type UndoHandlerContext = {
    onPatches: OnPatches;
    undo: () => LayoutEditPatch[];
    flushChanges: () => void;
};

const UndoHandlerContext = createContext<UndoHandlerContext | null>(null);

export const UndoHandlerProvider = ({ children }: { children: React.ReactNode }) => {
    const undoStateAtom = useMemoOne(
        () => atom<UndoState>({ undoStack: [], changesToCommit: [] }),
        []
    );

    const store = useStore();

    const flushChanges = useCallback(() => {
        store.set(
            undoStateAtom,
            produce(draft => {
                if (draft.changesToCommit.length === 0) {
                    return;
                }
                draft.undoStack.push(draft.changesToCommit.reverse());
                draft.changesToCommit = [];
            })
        );
    }, [store, undoStateAtom]);

    useEffect(() => {
        const intervalId = setInterval(flushChanges, 300);
        return () => clearInterval(intervalId);
    }, [flushChanges]);

    const undo = useCallback(() => {
        flushChanges();
        const oldValue = store.get(undoStateAtom);
        const patch = oldValue.undoStack[oldValue.undoStack.length - 1] ?? [];
        const newValue = produce(draft => {
            draft.undoStack.pop();
        });
        store.set(undoStateAtom, newValue);

        return patch;
    }, [flushChanges, store, undoStateAtom]);

    const onPatches = useMemo(() => {
        const onPatches =
            (block: string | null) => (_patches: Patch[], inversePatches: Patch[]) => {
                store.set(
                    undoStateAtom,
                    produce(draft => {
                        draft.changesToCommit.push({ block, inversePatches });
                    })
                );
            };

        return onPatches;
    }, [store, undoStateAtom]);

    return (
        <UndoHandlerContext.Provider value={{ undo, onPatches, flushChanges }}>
            {children}
        </UndoHandlerContext.Provider>
    );
};

export const useUndoHandler = () => {
    const context = useContext(UndoHandlerContext);

    if (!context) {
        throw new Error("useUndoHandler must be used within an UndoHandlerProvider");
    }

    return context;
};

export type OnPatches = (
    block: string | null
) => (patches: Patch[], inversePatches: Patch[]) => void;
